import React, { useState } from "react";
import { ArrowRight, Check, Users, CalendarDays, Sparkles } from "lucide-react";
import { SUMMARY, FOOTER_TAGLINE } from "../mock/data";
import { useReveal } from "../hooks/useReveal";
import { useToast } from "../hooks/use-toast";
import api from "../lib/api";

const STAGES = ["Just exploring", "Setting up my account", "My account is active"];

const BENEFITS = [
  { icon: Users, text: "A direct invite into the community once your answers are in" },
  { icon: CalendarDays, text: "The date and link for the next live walkthrough session" },
  { icon: Sparkles, text: "The full strategy breakdown, account structures and risks in writing" },
];

export default function RequestInfo() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "", phone: "", introducer: "", foundSelf: false,
    watched_webinar: "", stage: "", discord: "", email: "",
  });
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast({ title: "Almost there", description: "Please add your name and phone number." });
      return;
    }
    const payload = {
      name: form.name,
      phone: form.phone,
      introducer: form.foundSelf ? "I found the community myself" : form.introducer,
      watched_webinar: form.watched_webinar,
      stage: form.stage,
      discord: form.discord,
      email: form.email,
    };
    try {
      await api.post("/leads", payload);
    } catch (err) {
      const leads = JSON.parse(localStorage.getItem("copyx_leads") || "[]");
      leads.push({ ...payload, at: new Date().toISOString() });
      localStorage.setItem("copyx_leads", JSON.stringify(leads));
    }
    setSubmitted(true);
    toast({ title: "You're in", description: "Continue through to the community invite." });
  };

  const [ref, inView] = useReveal();

  const Pill = ({ active, onClick, children }) => (
    <button type="button" onClick={onClick}
      className={`px-4 py-2.5 rounded-lg label transition-colors ${active ? "bg-accent text-[#0a0d08]" : "bg-white/[0.03] text-[var(--muted)] hover:text-[var(--text)] border border-white/[0.08]"}`}>
      {children}
    </button>
  );

  const inputCls = "mt-2 w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-4 py-3 text-[var(--text)] text-[14px] focus:border-accent/50 outline-none transition-colors";

  return (
    <div>
      <section id="request-info" className="grid-bg border-t border-white/[0.07]">
        <div id="join" className="max-w-[1300px] mx-auto px-5 md:px-8 py-20 md:py-28 grid lg:grid-cols-2 gap-12">
          <div>
            <div className="flex items-center gap-2 mb-5">
              <span className="w-1.5 h-1.5 bg-accent" />
              <span className="label text-[var(--muted)]">Next step</span>
            </div>
            <h2 className="display text-[clamp(1.9rem,4.2vw,3.3rem)]">Join the <span className="text-accent">community</span></h2>
            <p className="mt-5 text-[15px] text-[var(--muted)] leading-relaxed max-w-lg">
              Five short questions, then you go straight through to the community invite — with the full strategy breakdown, the account structures and the next live session.
            </p>

            <div className="mt-8 space-y-4">
              {BENEFITS.map((b, i) => {
                const Icon = b.icon;
                return (
                  <div key={i} className="flex items-start gap-3">
                    <Icon size={18} className="text-accent mt-0.5 shrink-0" strokeWidth={1.6} />
                    <span className="text-[14px] text-[var(--muted)]">{b.text}</span>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 card rounded-2xl p-5 max-w-md">
              <div className="label text-accent mb-2">Live walkthrough sessions</div>
              <div className="text-[15px] text-[var(--text)] font-medium">Tuesday · Thursday · Sunday</div>
              <div className="text-[12px] text-[var(--muted)] mt-1">English-language sessions. No forex experience needed.</div>
            </div>
          </div>

          {/* Form */}
          <div className="card rounded-2xl p-7">
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-10">
                <div className="w-14 h-14 rounded-full bg-accent/15 flex items-center justify-center mb-5">
                  <Check size={28} className="text-accent" />
                </div>
                <h3 className="display text-2xl mb-2">You're in, {form.name.split(" ")[0]}</h3>
                <p className="text-[14px] text-[var(--muted)] max-w-sm mb-6">
                  Thanks for the answers. Continue through to the community invite and you'll get the next session details.
                </p>
                <button onClick={() => toast({ title: "Community invite", description: "Your personal invite link would open here." })}
                  className="btn-accent px-6 py-3.5 rounded-lg inline-flex items-center gap-2">
                  Continue to the community <ArrowRight size={14} />
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label className="label text-[var(--muted)] text-[0.6rem]">Your name</label>
                  <input value={form.name} onChange={set("name")} placeholder="Full name" className={inputCls} />
                </div>

                <div>
                  <label className="label text-[var(--muted)] text-[0.6rem]">Phone number</label>
                  <input value={form.phone} onChange={set("phone")} placeholder="+00 000 000 000" className={inputCls} />
                  <p className="text-[11px] text-[var(--muted)] mt-1.5">Include your country code so the message reaches you.</p>
                </div>

                <div>
                  <label className="label text-[var(--muted)] text-[0.6rem]">Who introduced you?</label>
                  <input value={form.introducer} onChange={set("introducer")} disabled={form.foundSelf}
                    placeholder="Name of who introduced you" className={`${inputCls} disabled:opacity-40`} />
                  <label className="flex items-center gap-2 cursor-pointer mt-2.5">
                    <input type="checkbox" checked={form.foundSelf}
                      onChange={(e) => setForm({ ...form, foundSelf: e.target.checked })}
                      className="accent-[#c4f542] w-4 h-4" />
                    <span className="text-[12px] text-[var(--muted)]">I found the community myself</span>
                  </label>
                </div>

                <div>
                  <label className="label text-[var(--muted)] text-[0.6rem] block mb-2">Have you watched the webinar?</label>
                  <div className="flex gap-2">
                    <Pill active={form.watched_webinar === "Yes"} onClick={() => setForm({ ...form, watched_webinar: "Yes" })}>Yes</Pill>
                    <Pill active={form.watched_webinar === "No"} onClick={() => setForm({ ...form, watched_webinar: "No" })}>No</Pill>
                  </div>
                </div>

                <div>
                  <label className="label text-[var(--muted)] text-[0.6rem] block mb-2">Where are you currently?</label>
                  <div className="flex flex-wrap gap-2">
                    {STAGES.map((s) => (
                      <Pill key={s} active={form.stage === s} onClick={() => setForm({ ...form, stage: s })}>{s}</Pill>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="label text-[var(--muted)] text-[0.6rem]">Discord username (optional)</label>
                  <input value={form.discord} onChange={set("discord")} placeholder="username" className={inputCls} />
                </div>

                <div>
                  <label className="label text-[var(--muted)] text-[0.6rem]">Email (optional)</label>
                  <input value={form.email} onChange={set("email")} type="email" placeholder="you@email.com" className={inputCls} />
                </div>

                <button type="submit" className="btn-accent w-full py-3.5 rounded-lg inline-flex items-center justify-center gap-2">
                  Continue to the community <ArrowRight size={14} />
                </button>
                <p className="text-[11px] text-[var(--muted)]/70 leading-relaxed">
                  Your details are used only to send you this information and the community invite. They are not sold, and no account is opened on your behalf. This is not personalised investment advice.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Summary */}
      <section className="max-w-[1300px] mx-auto px-5 md:px-8 py-20 md:py-28">
        <div className="flex items-center gap-2 mb-5">
          <span className="w-1.5 h-1.5 bg-accent" />
          <span className="label text-[var(--muted)]">In summary</span>
        </div>
        <h2 className="display text-[clamp(1.9rem,4.2vw,3.3rem)] mb-12">What this <span className="text-accent">actually is</span></h2>
        <div ref={ref} className={`reveal ${inView ? "in" : ""} grid md:grid-cols-2 lg:grid-cols-3 gap-5`}>
          {SUMMARY.map((s, i) => (
            <div key={i} className="card rounded-2xl p-7">
              <div className="mono text-accent text-sm mb-4">0{i + 1}</div>
              <p className="text-[14px] text-[var(--muted)] leading-relaxed">{s}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/[0.07] bg-[#0b0f0a]">
        <div className="max-w-[1300px] mx-auto px-5 md:px-8 py-14">
          <div className="display text-lg mb-4">COPYX <span className="text-accent text-sm font-normal mono">×</span> SONIC AI</div>
          <p className="max-w-3xl leading-tight text-[clamp(1.3rem,3vw,2rem)] font-extrabold tracking-tight text-[var(--text)]" style={{ fontFamily: "'Archivo', sans-serif" }}>
            {FOOTER_TAGLINE}
          </p>
          <p className="mt-8 text-[12px] text-[var(--muted)] max-w-3xl leading-relaxed">
            Educational information only. This page does not provide personalised investment advice. Trading financial markets involves risk and may result in the loss of capital. Past performance is not indicative of future results.
          </p>
          <div className="mt-8 pt-6 border-t border-white/[0.06] flex flex-wrap items-center justify-between gap-4">
            <span className="text-[11px] text-[var(--muted)]/70">© {new Date().getFullYear()} CopyX × Sonic AI. Illustrative clone for demonstration.</span>
            <div className="flex gap-5">
              {["How it works", "Performance", "Risk", "FAQ"].map((l) => (
                <a key={l} href={`#${l.toLowerCase().replace(/ /g, "")}`} className="label text-[var(--muted)] hover:text-[var(--text)] transition-colors">{l}</a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
